/**
 * GrowMyRestaurantWelcome — first-time-after-Go-Live welcome popup
 * + Dashboard reminder card.
 *
 * The popup is shown ONCE per restaurant — it remembers dismissal via
 * localStorage (`nofee_grow_welcome_seen_<restaurantId>`) and also disappears
 * automatically once the restaurant has submitted its first campaign.
 *
 * The reminder card is shown on the dashboard until the first campaign
 * request is submitted (then it disappears permanently).
 *
 * Trigger conditions for the popup:
 *   - restaurant.go_live_at is set (Go Live process completed), AND
 *   - localStorage flag not set, AND
 *   - status.has_submitted_first === false
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Rocket, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'nofee_token';

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function GrowMyRestaurantWelcome({ restaurant, variant = 'both' }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  const restaurantId = restaurant?.id;
  const wentLive = !!restaurant?.go_live_at;
  const seenKey = restaurantId ? `nofee_grow_welcome_seen_${restaurantId}` : null;

  // Fetch campaign status to decide whether to show.
  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/marketing/status`, { headers: authHeaders() });
        if (!cancelled) setStatus(data);
      } catch {
        /* silent — non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  // Decide popup visibility.
  useEffect(() => {
    if (!wentLive || !status || !seenKey) return;
    const alreadySeen = typeof window !== 'undefined' && window.localStorage.getItem(seenKey) === '1';
    if (!alreadySeen && !status.has_submitted_first) {
      setPopupOpen(true);
    }
  }, [wentLive, status, seenKey]);

  function dismissPopup() {
    if (seenKey && typeof window !== 'undefined') {
      window.localStorage.setItem(seenKey, '1');
    }
    setPopupOpen(false);
  }

  function goToGrow() {
    dismissPopup();
    navigate('/dashboard/grow');
  }

  // Don't render the reminder card if the restaurant has already submitted.
  const showReminderCard =
    (variant === 'card' || variant === 'both') &&
    status &&
    status.has_submitted_first === false;

  const showPopup = (variant === 'popup' || variant === 'both') && popupOpen;

  return (
    <>
      {/* Smart reminder card (overview) */}
      {showReminderCard && (
        <div
          data-testid="grow-reminder-card"
          className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl border border-fuchsia-500/30 bg-gradient-to-r from-fuchsia-500/[0.10] via-purple-500/[0.06] to-amber-500/[0.08] hover:border-fuchsia-500/50 transition-all"
        >
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500/30 to-amber-500/30 border border-fuchsia-500/40 flex items-center justify-center shrink-0">
              <span className="text-xl">🚀</span>
            </div>
            <div className="min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-fuchsia-300 font-bold">Included with your membership</p>
              <p className="text-[15px] font-extrabold text-white mt-0.5">Grow Your Restaurant</p>
              <p className="text-[12.5px] text-zinc-300/85 leading-relaxed mt-1 max-w-xl">
                Need more direct customers? Your first personalized marketing campaign is included with your membership.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard/grow')}
            data-testid="grow-reminder-cta"
            className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-black font-extrabold text-[13px] hover:bg-white/90 transition-colors"
          >
            <Rocket className="w-4 h-4" /> Grow My Restaurant
          </button>
        </div>
      )}

      {/* Welcome popup */}
      {showPopup && (
        <div
          data-testid="grow-welcome-popup"
          className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="grow-welcome-title"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={dismissPopup}
          />
          {/* Card */}
          <div className="relative w-full max-w-md rounded-3xl border border-fuchsia-500/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 shadow-2xl p-7 sm:p-9 text-center">
            <button
              onClick={dismissPopup}
              data-testid="grow-welcome-dismiss"
              className="absolute top-3 right-3 w-9 h-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500/30 to-amber-500/30 border border-fuchsia-500/40 items-center justify-center mb-5">
              <span className="text-3xl">🎉</span>
            </div>
            <h2 id="grow-welcome-title" className="text-2xl sm:text-[26px] font-extrabold text-white tracking-tight leading-tight">
              Congratulations!
            </h2>
            <p className="mt-3 text-sm text-white/80 leading-relaxed">
              Your restaurant is now <span className="text-gold-300 font-semibold">live</span>.
            </p>
            <p className="mt-4 text-sm text-white/75 leading-relaxed">
              Ready to attract more direct customers? NoFee can help create personalized marketing campaigns for your restaurant.
            </p>
            <button
              onClick={goToGrow}
              data-testid="grow-welcome-cta"
              className="mt-7 inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl bg-gold-500 text-black font-extrabold text-[14px] hover:bg-gold-400 transition-colors"
            >
              <Rocket className="w-4 h-4" /> Grow My Restaurant
            </button>
            <button
              onClick={dismissPopup}
              className="mt-3 text-[12px] text-zinc-400 hover:text-white transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </>
  );
}
